import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HandleTableData,
  useTableExport,
} from '@wajeez-web/components/data-table';
import { Button, InnerPageToggleButton } from '@wajeez-web/components/ui';
import { Identity } from '@wajeez-web/core/api-base';
import {
  useBusinessSelector,
  BusinessUsersActions,
  useBusinessDispatch,
} from '@wajeez-web/stores/business-group';
import {
  AlertType,
  CommonActions,
  useCommonDispatch,
} from '@wajeez-web/stores/common';
import { AiOutlinePlus } from 'react-icons/ai';
import {
  ChangeUserRoleModal,
  AddUserModal,
} from '@wajeez-web/components/modals';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import { CSVLink } from 'react-csv';
import { DownloadIcon } from '@heroicons/react/outline';
import { ConfirmationModal } from '@wajeez-web/components/ui';

const BusinessUsers = ({ id, title }: { id: number; title: string }) => {
  const exportDataRef = useRef<any>(null);

  const businessDispatch = useBusinessDispatch();
  const commonDispatch = useCommonDispatch();
  const { t } = useTranslation();

  const showUserIdColumnAlert = () =>
    commonDispatch(
      CommonActions.setAlert({
        for: 'page',
        show: true,
        type: AlertType.ERROR,
        messages: [
          {
            code: 0,
            field: null,
            message: 'The UserId column should be visible',
          },
        ],
      })
    );

  const [UserModalState, setUserModalState] = useState(false);
  const [AdminModalState, setAdminModalState] = useState(false);
  const [userRoleModalState, setuserRoleModalState] = useState<{
    state: boolean;
    selected: number;
    userRole: Identity.BusinessGroupUserRole;
  }>({
    state: false,
    selected: -1,
    userRole: Identity.BusinessGroupUserRole.User,
  });
  const [unlinkUserConfirmationModal, setUnlinkUserConfirmationModal] =
    useState<{ isOpen: boolean; message: string }>();
  const [selectedUser, setSelectedUser] = useState<{ id: string }>();
  const [resendWelcomeUserId, setResendWelcomeUserId] = useState<string>();

  const data = useBusinessSelector((state) => state.businessGroupsUsers.data);
  const status = useBusinessSelector(
    (state) => state.businessGroupsUsers.status
  );
  const dataExported = useBusinessSelector(
    ({ businessGroupsUsers }) => businessGroupsUsers.datatableExported
  );
  const resendWelcomeStatus = useBusinessSelector(
    ({ businessGroupsUsers }) => businessGroupsUsers.resendWelcome.status
  );
  const unlinkUserStatus = useBusinessSelector(
    ({ businessGroupsUsers }) =>
      businessGroupsUsers.unlinkBusinessGroupUser.status
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'businessSubscription',
    BusinessUsersActions.getBusinessGroupUserDatatableAsync,
    undefined,
    { businessGroupId: id }
  );

  const { getExportedData, mapExportedData } = useTableExport(
    data?.totalCount,
    dataExported.data as contentEntityDataType,
    BusinessUsersActions.getBusinessGroupUserDatatableExportedAsync,
    tablePreferances,
    exportDataRef
  );

  return (
    <>
      <CSVLink
        filename={'B2B_Users.csv'}
        data={mapExportedData()}
        ref={exportDataRef}
        className="hidden"
      >
        Download me
      </CSVLink>
      {userRoleModalState.state && (
        <ChangeUserRoleModal
          data={userRoleModalState.userRole}
          isOpen={userRoleModalState.state}
          onClose={() =>
            setuserRoleModalState({
              state: false,
              selected: -1,
              userRole: Identity.BusinessGroupUserRole.User,
            })
          }
          onSubmit={async (userRole) => {
            const res = await businessDispatch(
              BusinessUsersActions.setBusinessGroupUserRoleAsync({
                businessGroupUserId: userRoleModalState.selected,
                userRole: userRole,
              })
            );
            if (res.meta.requestStatus === 'fulfilled') {
              setuserRoleModalState({
                state: false,
                selected: -1,
                userRole: Identity.BusinessGroupUserRole.User,
              });
              getData();
            }
          }}
        />
      )}
      {UserModalState && (
        <AddUserModal
          isOpen={UserModalState}
          onClose={() => {
            setUserModalState(false);
          }}
          onSubmit={async (businessCreateNewUserValues) => {
            if (businessCreateNewUserValues) {
              const res = await businessDispatch(
                BusinessUsersActions.createNewBusinessGroupUserAsync({
                  businessGroupId: Number(id),
                  ...businessCreateNewUserValues,
                })
              );
              if (res.meta.requestStatus === 'fulfilled') {
                setUserModalState(false);
                getData();
              }
            }
          }}
        />
      )}
      {AdminModalState && (
        <AddUserModal
          isOpen={AdminModalState}
          onClose={() => {
            setAdminModalState(false);
          }}
          onSubmit={async (businessCreateNewUserValues) => {
            if (businessCreateNewUserValues) {
              const res = await businessDispatch(
                BusinessUsersActions.createNewBusinessGroupAdminAsync({
                  businessGroupId: Number(id),
                  ...businessCreateNewUserValues,
                })
              );

              if (res.meta.requestStatus === 'fulfilled') {
                setAdminModalState(false);
                getData();
              }
            }
          }}
          title="Add Admin"
        />
      )}
      <div className="space-y-6">
        <div className="flex justify-between">
          <div>
            <h3 className="ms-2 lg:ms-0 text-2xl flex align-middle font-bold space-s-2 tracking-tight leading-none  ">
              {<InnerPageToggleButton />} <div className="mt-1">{title}</div>
            </h3>
            <p className=" text-sm text-gray-600"></p>
          </div>
          <div className="flex justify-end items-center space-s-2">
            <Button
              color="white"
              title="Add New User"
              bg="primary"
              Icon={AiOutlinePlus}
              onClick={() => {
                setUserModalState(true);
              }}
            />
            <Button
              color="white"
              title="Add Admin"
              bg="primary"
              Icon={AiOutlinePlus}
              onClick={() => {
                setAdminModalState(true);
              }}
            />
          </div>
        </div>
        <DataTableWrapper
          data={data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Change Role',
                permission: '91.2',
                type: 'request',
                endpoint: '',
                action: async () => {
                  setuserRoleModalState({
                    state: true,
                    selected: Number(row[1]),
                    userRole: parseInt(row[32] as string),
                  });
                },
              },
              {
                title: 'Resend Welcome Email',
                permission: '91.1',
                type: 'request',
                endpoint: '',
                action: async () => {
                  if (row[2]) {
                    setResendWelcomeUserId(row[2] as string);
                  } else {
                    showUserIdColumnAlert();
                  }
                },
              },
              {
                title: 'Unlink',
                permission: '91.18',
                type: 'request',
                endpoint: '',
                action: async () => {
                  if (row[2]) {
                    setSelectedUser({ id: row[2] as string });

                    setUnlinkUserConfirmationModal({
                      isOpen: true,
                      message:
                        'Are you sure that you want to unlink this user?',
                    });
                  } else {
                    showUserIdColumnAlert();
                  }
                },
              },
            ];
          }}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
          extraActions={[
            {
              label: 'Export as CSV',
              onClick: () => {
                getExportedData();
              },
              Icon: <DownloadIcon className="h-4 w-4" />,
            },
          ]}
        />
      </div>
      {resendWelcomeUserId && (
        <ConfirmationModal
          isOpen={!!resendWelcomeUserId}
          message="Are you sure you want to resend the welcome email to this user?"
          onClose={() => setResendWelcomeUserId(undefined)}
          onSubmit={async () => {
            const res = await businessDispatch(
              BusinessUsersActions.resendBusinessGroupUserWelcomeAsync({
                businessGroupId: id,
                userId: resendWelcomeUserId,
              })
            );

            setResendWelcomeUserId(undefined);

            if (res.meta.requestStatus === 'fulfilled') {
              commonDispatch(
                CommonActions.setAlert({
                  for: 'page',
                  show: true,
                  type: AlertType.SUCCESS,
                  messages: [
                    {
                      code: 0,
                      field: null,
                      message: t(`general.${'The welcome email was sent'}`, ''),
                    },
                  ],
                })
              );
            }
          }}
          isLoading={resendWelcomeStatus === 'loading'}
        />
      )}
      {unlinkUserConfirmationModal?.isOpen && (
        <ConfirmationModal
          isOpen={unlinkUserConfirmationModal.isOpen}
          message={unlinkUserConfirmationModal.message}
          onClose={() => {
            setUnlinkUserConfirmationModal({ isOpen: false, message: '' });
          }}
          onSubmit={async () => {
            const res = await businessDispatch(
              BusinessUsersActions.unlinkBusinessGroupUserAsync({
                businessGroupId: id,
                userId: selectedUser?.id,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setUnlinkUserConfirmationModal({ isOpen: false, message: '' });

              tablePreferances && setTablePreferences({ ...tablePreferances });
            }
          }}
          isLoading={unlinkUserStatus === 'loading'}
        />
      )}
    </>
  );
};

export default BusinessUsers;
